// Spot the Error — React port of kula-create's spot-the-error.html.
//
// Pedagogy: critical evaluation. Learner finds factual errors embedded in
// otherwise-plausible content. Decoys (correct but suspicious-sounding bits)
// test the learner's discrimination, not just their suspicion.
//
// Fires onActivityResponse once when the learner declares the final section
// done, with
//   { found: [errorId, ...], missed: [errorId, ...], falsePositives: [chunkKey, ...] }
// where `found` is every error caught, `missed` is every error NOT caught,
// and `falsePositives` is every non-error chunk wrongly flagged. The
// registry's answerKey is the set of error ids across all sections —
// scoring is set-equality.

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { RotateCcw } from 'lucide-react';
import type { SpotTheErrorSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';
import { NeonOracleSpotTheError } from './NeonOracleSpotTheError';

interface Props {
  data: SpotTheErrorSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: SpotTheErrorSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function SpotTheErrorSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete, savedResponse }: Props) {
  if (isEditing) {
    return <SpotTheErrorEditor data={data} theme={theme} onUpdate={onUpdate} />;
  }
  // Tier-A bespoke renderer for the "Neon Oracle" world (home-set pairing #2).
  // It accepts onComplete + savedResponse and fires onComplete once when the
  // learner finalises the diagnostic (the terminal section).
  if (theme.id === 'neon-oracle') {
    return (
      <NeonOracleSpotTheError
        data={data}
        theme={theme}
        onActivityResponse={onActivityResponse}
        onComplete={onComplete}
        savedResponse={savedResponse}
      />
    );
  }
  return (
    <SpotTheErrorStudent
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

// ---------------------------------------------------------------------------
// Student-facing renderer
// ---------------------------------------------------------------------------

function SpotTheErrorStudent({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: SpotTheErrorSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const success = '#16a34a';

  // A saved payload is only written when the learner finishes the final
  // section, so its presence means "restore the submitted end-state".
  const savedDone = !!savedResponse && Array.isArray(savedResponse.found);

  const [currentSectionIdx, setCurrentSectionIdx] = useState(
    savedDone ? Math.max(0, data.sections.length - 1) : 0,
  );
  const [foundErrors, setFoundErrors] = useState<Set<string>>(
    () =>
      new Set(
        savedDone
          ? (savedResponse!.found as unknown[]).filter((id): id is string => typeof id === 'string')
          : [],
      ),
  );
  const [falsePositives, setFalsePositives] = useState<Set<string>>(
    () =>
      new Set(
        savedDone && Array.isArray(savedResponse!.falsePositives)
          ? (savedResponse!.falsePositives as unknown[]).filter((id): id is string => typeof id === 'string')
          : [],
      ),
  );
  const [flash, setFlash] = useState<{ key: string; message: string } | null>(null);
  const [allDone, setAllDone] = useState(savedDone);
  const completedRef = useRef(savedDone);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    },
    [],
  );

  const sectionErrorIds = useMemo(
    () =>
      data.sections.map((s) =>
        s.content
          .filter((c) => c.type === 'error' && c.errorId)
          .map((c) => c.errorId!) as string[],
      ),
    [data.sections],
  );

  // Zero-error sections are vacuously complete — the declare-done button
  // advances them either way.
  const isSectionComplete = (idx: number): boolean => {
    const ids = sectionErrorIds[idx] ?? [];
    return ids.every((id) => foundErrors.has(id));
  };

  const handleErrorClick = useCallback(
    (errorId: string) => {
      if (foundErrors.has(errorId)) return;
      setFoundErrors((prev) => new Set(prev).add(errorId));
    },
    [foundErrors],
  );

  // Wrong click — plain text, a decoy, or a malformed error chunk. Brief
  // neutral feedback; decoys surface their authored explanation.
  const handleWrongClick = useCallback((chunkKey: string, message: string) => {
    setFalsePositives((prev) => new Set(prev).add(chunkKey));
    setFlash({ key: chunkKey, message });
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = setTimeout(
      () => setFlash((curr) => (curr?.key === chunkKey ? null : curr)),
      1600,
    );
  }, []);

  const advanceSection = useCallback(() => {
    if (currentSectionIdx < data.sections.length - 1) {
      setCurrentSectionIdx((i) => i + 1);
    } else {
      // Learner declared the last section done — fire response.
      setAllDone(true);
      const allErrorIds = sectionErrorIds.flat();
      onActivityResponse?.({
        activityType: 'spot-the-error',
        mode: 'quiz',
        responseData: {
          found: allErrorIds.filter((id) => foundErrors.has(id)),
          missed: allErrorIds.filter((id) => !foundErrors.has(id)),
          falsePositives: Array.from(falsePositives),
        },
      });
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
    }
  }, [currentSectionIdx, data.sections.length, sectionErrorIds, foundErrors, falsePositives, onActivityResponse, onComplete]);

  // Replay — clear the learner's marks and return to the first section so they
  // can re-audit the passage. The recorded completion stands; this is practice.
  const tryAgain = useCallback(() => {
    setFoundErrors(new Set());
    setFalsePositives(new Set());
    setFlash(null);
    setAllDone(false);
    setCurrentSectionIdx(0);
  }, []);

  const totalErrors = sectionErrorIds.reduce((acc, ids) => acc + ids.length, 0);
  const totalFound = sectionErrorIds.flat().filter((id) => foundErrors.has(id)).length;

  // Authored "So what?" close (origin/main): a Tier-2 framing rendered once the
  // learner finishes the whole activity. Compatible with declare-done — it
  // surfaces whenever `allDone` is set, however the learner reached the end.
  const conclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'So what',
    body: 'You caught {found} of {total} planted errors.',
  };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="text-center mb-8">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && (
            <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>
          )}
        </header>

        <div className="h-1 rounded-full mb-8 overflow-hidden" style={{ background: theme.colors.surface }}>
          <div
            className="h-full transition-all duration-500"
            style={{
              background: accent,
              width: `${totalErrors > 0 ? Math.round((totalFound / totalErrors) * 100) : 0}%`,
            }}
          />
        </div>

        {data.sections.map((section, sIdx) => {
          if (sIdx > currentSectionIdx) return null;
          const errorIds = sectionErrorIds[sIdx] ?? [];
          const sectionFoundCount = errorIds.filter((id) => foundErrors.has(id)).length;
          const isComplete = isSectionComplete(sIdx);
          const isCurrent = sIdx === currentSectionIdx;
          const locked = !isCurrent || allDone;

          return (
            <section
              key={section.id}
              className="mb-6"
              data-section-id={section.id}
              style={{ opacity: !isCurrent && isComplete ? 0.7 : 1 }}
            >
              <div
                className="rounded-lg p-6 border"
                style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-block text-xs font-bold tracking-wider uppercase" style={{ color: accent }}>
                    {section.instruction}
                  </span>
                  <span
                    className="text-xs font-semibold px-3 py-1 rounded-full"
                    style={{
                      background: isComplete ? `${success}15` : theme.colors.background,
                      color: isComplete ? success : theme.colors.textMuted,
                    }}
                    aria-live="polite"
                  >
                    Found: {sectionFoundCount} of {errorIds.length}
                  </span>
                </div>
                <p className="leading-relaxed text-sm mb-4" style={{ color: theme.colors.textMuted }}>
                  {section.context}
                </p>

                <div
                  className="leading-relaxed"
                  style={{ color: theme.colors.text, lineHeight: 1.75 }}
                >
                  {section.content.map((chunk, cIdx) => {
                    if (chunk.type === 'error' && chunk.errorId) {
                      const isFound = foundErrors.has(chunk.errorId);
                      return (
                        <ChunkSpan
                          key={cIdx}
                          chunkText={chunk.text}
                          isFound={isFound}
                          foundExplanation={isFound ? chunk.explanation : undefined}
                          flashMessage={null}
                          disabled={locked}
                          success={success}
                          theme={theme}
                          onClick={() => handleErrorClick(chunk.errorId!)}
                        />
                      );
                    }
                    // Plain text, decoys, and malformed error chunks (missing
                    // errorId) all render — and behave — identically, so
                    // nothing gives the answers away before a click.
                    const chunkKey = `${section.id}:${cIdx}`;
                    return (
                      <ChunkSpan
                        key={cIdx}
                        chunkText={chunk.text}
                        isFound={false}
                        flashMessage={flash?.key === chunkKey ? flash.message : null}
                        disabled={locked}
                        success={success}
                        theme={theme}
                        onClick={() =>
                          handleWrongClick(
                            chunkKey,
                            chunk.type === 'decoy' && chunk.explanation
                              ? chunk.explanation
                              : 'Nothing wrong here.',
                          )
                        }
                      />
                    );
                  })}
                </div>

                <AnimatePresence>
                  {isComplete && (
                    <Reveal
                      variant="fade"
                      className="mt-4 px-4 py-3 rounded text-sm"
                      style={{ background: `${success}15`, color: success }}
                    >
                      ✓ {section.completionMessage}
                    </Reveal>
                  )}
                </AnimatePresence>

                {isCurrent && !allDone && (
                  <button
                    onClick={advanceSection}
                    className="mt-3 px-6 py-3 rounded-lg font-semibold text-sm w-full"
                    style={{ background: accent, color: '#fff' }}
                  >
                    {isComplete
                      ? sIdx < data.sections.length - 1
                        ? 'Next section'
                        : 'Finish'
                      : sIdx < data.sections.length - 1
                        ? "I've found them all — next section"
                        : "I've found them all — finish"}
                  </button>
                )}
              </div>
            </section>
          );
        })}

        <AnimatePresence>
          {allDone && (
            <Reveal
              variant="scale"
              spring="bouncy"
              className="rounded-lg border-2 p-6 mt-6 text-center"
              style={{ background: theme.colors.surface, borderColor: accent }}
            >
              <span className="inline-block text-xs font-bold tracking-wider uppercase mb-2" style={{ color: accent }}>
                Done
              </span>
              <h2 style={{ ...slideTypographyStyle(theme, 'subheading'), color: accent, fontWeight: 700, marginBottom: 8 }}>
                {totalFound} of {totalErrors} errors found
              </h2>
              {totalErrors - totalFound > 0 && (
                <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem' }}>
                  You missed {totalErrors - totalFound} error{totalErrors - totalFound === 1 ? '' : 's'}.
                </p>
              )}
              {falsePositives.size > 0 && (
                <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem' }}>
                  You also flagged {falsePositives.size} passage{falsePositives.size === 1 ? '' : 's'} that {falsePositives.size === 1 ? 'was' : 'were'} actually correct.
                </p>
              )}
            </Reveal>
          )}
        </AnimatePresence>

        {/* Authored "So what?" close. */}
        <AnimatePresence>
          {allDone && (
            <Reveal variant="up" className="mt-4">
              <ExperienceClose
                conclusion={conclusion}
                theme={theme}
                values={{ found: totalFound, total: totalErrors }}
                fallback="Look back at which claims you flagged and which slipped past."
              />
            </Reveal>
          )}
        </AnimatePresence>

        {/* Replay CTA — re-audit the passage. Advancing to the next slide is the
            lesson nav's job; this gives a clear in-experience action at the end. */}
        <AnimatePresence>
          {allDone && (
            <Reveal variant="fade" className="mt-4 flex justify-center">
              <button
                onClick={tryAgain}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                style={{ border: `1px solid ${accent}`, color: accent, background: 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}12`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <RotateCcw className="w-4 h-4" />
                Try again
              </button>
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Single renderer for ALL chunk types — text, error, and decoy must be
// visually and behaviourally indistinguishable until clicked, otherwise the
// styling itself gives the answers away.
function ChunkSpan({
  chunkText,
  isFound,
  foundExplanation,
  flashMessage,
  disabled,
  success,
  theme,
  onClick,
}: {
  chunkText: string;
  isFound: boolean;
  foundExplanation?: string;
  flashMessage: string | null;
  disabled: boolean;
  success: string;
  theme: Theme;
  onClick: () => void;
}) {
  const interactive = !disabled && !isFound;
  return (
    <span className="relative inline">
      <span
        role="button"
        tabIndex={interactive ? 0 : -1}
        aria-pressed={isFound}
        onClick={() => interactive && onClick()}
        onKeyDown={(e) => {
          if (!interactive) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        style={{
          cursor: interactive ? 'pointer' : 'default',
          padding: isFound || flashMessage ? '2px 4px' : 0,
          margin: '0 1px',
          borderRadius: 3,
          background: isFound ? `${success}15` : flashMessage ? `${theme.colors.textMuted}22` : 'transparent',
          color: isFound ? success : 'inherit',
          transition: 'background 200ms, color 200ms',
          textDecoration: isFound ? 'line-through' : 'none',
          textDecorationColor: isFound ? success : 'transparent',
          fontWeight: isFound ? 600 : 'inherit',
        }}
      >
        {chunkText}
      </span>
      {isFound && foundExplanation && (
        <span
          className="block mt-1 mb-2 px-3 py-2 rounded text-sm leading-relaxed"
          style={{ background: `${success}10`, color: theme.colors.text, borderLeft: `3px solid ${success}` }}
        >
          <strong style={{ color: success }}>Why this is wrong:</strong> {foundExplanation}
        </span>
      )}
      {!isFound && flashMessage && (
        <span
          className="block mt-1 mb-2 px-3 py-2 rounded text-sm leading-relaxed"
          style={{
            background: `${theme.colors.textMuted}15`,
            color: theme.colors.textMuted,
            borderLeft: `3px solid ${theme.colors.textMuted}66`,
          }}
        >
          {flashMessage}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Editing-mode renderer
// ---------------------------------------------------------------------------

function SpotTheErrorEditor({
  data,
  theme,
  onUpdate,
}: {
  data: SpotTheErrorSlideData;
  theme: Theme;
  onUpdate: (data: SpotTheErrorSlideData) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;

  const updateField = <K extends keyof SpotTheErrorSlideData>(
    key: K,
    value: SpotTheErrorSlideData[K],
  ) => onUpdate({ ...data, [key]: value });

  const updateSection = (
    id: string,
    patch: Partial<SpotTheErrorSlideData['sections'][number]>,
  ) =>
    onUpdate({
      ...data,
      sections: data.sections.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    });

  const addSection = () =>
    onUpdate({
      ...data,
      sections: [
        ...data.sections,
        {
          id: crypto.randomUUID(),
          instruction: '',
          context: '',
          content: [],
          completionMessage: '',
        },
      ],
    });

  const removeSection = (id: string) =>
    onUpdate({ ...data, sections: data.sections.filter((s) => s.id !== id) });

  const updateChunk = (
    sectionId: string,
    chunkIdx: number,
    patch: Partial<SpotTheErrorSlideData['sections'][number]['content'][number]>,
  ) => {
    const section = data.sections.find((s) => s.id === sectionId);
    if (!section) return;
    const next = section.content.map((c, idx) => (idx === chunkIdx ? { ...c, ...patch } : c));
    updateSection(sectionId, { content: next });
  };

  const addChunk = (sectionId: string, type: 'text' | 'error' | 'decoy') => {
    const section = data.sections.find((s) => s.id === sectionId);
    if (!section) return;
    const baseChunk: SpotTheErrorSlideData['sections'][number]['content'][number] =
      type === 'error'
        ? { type: 'error', text: '', errorId: crypto.randomUUID(), explanation: '' }
        : { type, text: '' };
    updateSection(sectionId, { content: [...section.content, baseChunk] });
  };

  const removeChunk = (sectionId: string, chunkIdx: number) => {
    const section = data.sections.find((s) => s.id === sectionId);
    if (!section) return;
    updateSection(sectionId, { content: section.content.filter((_, idx) => idx !== chunkIdx) });
  };

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[760px]">
        <input
          value={data.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Spot the error title…"
          className="w-full mb-2 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => updateField('subtitle', e.target.value)}
          placeholder="Subtitle"
          className="w-full mb-6 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm transition-colors"
          style={{ color: theme.colors.textMuted }}
        />

        {data.sections.map((section, sIdx) => (
          <div
            key={section.id}
            className="rounded-lg border p-5 mb-4"
            style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold tracking-wider uppercase" style={{ color: accent }}>
                Section {sIdx + 1}
              </span>
              <button onClick={() => removeSection(section.id)} className="text-xs text-gray-400 hover:text-red-500">
                Remove
              </button>
            </div>
            <input
              value={section.instruction}
              onChange={(e) => updateSection(section.id, { instruction: e.target.value })}
              placeholder="Instruction (e.g. ‘Read this AI summary’)"
              className="w-full mb-2 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none font-semibold text-sm"
              style={{ color: theme.colors.text }}
            />
            <textarea
              value={section.context}
              onChange={(e) => updateSection(section.id, { context: e.target.value })}
              placeholder="Context (what to look for)"
              rows={2}
              className="w-full mb-3 px-3 py-2 rounded border text-sm"
              style={{
                background: theme.colors.background,
                borderColor: `${theme.colors.textMuted}33`,
                color: theme.colors.text,
              }}
            />

            {section.content.map((chunk, cIdx) => (
              <div
                key={cIdx}
                className="rounded p-3 mb-2"
                style={{
                  background: theme.colors.background,
                  border: `1px solid ${
                    chunk.type === 'error' ? `${accent}66` : chunk.type === 'decoy' ? '#fbbf2466' : `${theme.colors.textMuted}22`
                  }`,
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className="text-xs font-bold uppercase tracking-wider"
                    style={{
                      color: chunk.type === 'error' ? accent : chunk.type === 'decoy' ? '#b45309' : theme.colors.textMuted,
                    }}
                  >
                    {chunk.type}
                  </span>
                  <button
                    onClick={() => removeChunk(section.id, cIdx)}
                    className="text-xs text-gray-400 hover:text-red-500"
                  >
                    ×
                  </button>
                </div>
                <textarea
                  value={chunk.text}
                  onChange={(e) => updateChunk(section.id, cIdx, { text: e.target.value })}
                  placeholder={
                    chunk.type === 'text'
                      ? 'Normal text'
                      : chunk.type === 'error'
                        ? 'Text containing the error'
                        : 'Suspicious-looking but actually correct text'
                  }
                  rows={2}
                  className="w-full px-2 py-1 rounded border text-sm"
                  style={{
                    background: theme.colors.surface,
                    borderColor: `${theme.colors.textMuted}33`,
                    color: theme.colors.text,
                  }}
                />
                {chunk.type === 'error' && (
                  <textarea
                    value={chunk.explanation ?? ''}
                    onChange={(e) => updateChunk(section.id, cIdx, { explanation: e.target.value })}
                    placeholder="Explanation — why is this wrong?"
                    rows={2}
                    className="w-full mt-2 px-2 py-1 rounded border text-sm"
                    style={{
                      background: theme.colors.surface,
                      borderColor: `${theme.colors.textMuted}33`,
                      color: theme.colors.text,
                    }}
                  />
                )}
              </div>
            ))}

            <div className="flex gap-2 mb-2 text-xs">
              <button onClick={() => addChunk(section.id, 'text')} style={{ color: theme.colors.textMuted }}>
                + Text
              </button>
              <button onClick={() => addChunk(section.id, 'error')} style={{ color: accent }}>
                + Error
              </button>
              <button onClick={() => addChunk(section.id, 'decoy')} style={{ color: '#b45309' }}>
                + Decoy
              </button>
            </div>

            <input
              value={section.completionMessage}
              onChange={(e) => updateSection(section.id, { completionMessage: e.target.value })}
              placeholder="Completion message (shown when all errors in this section are found)"
              className="w-full mt-2 px-3 py-2 rounded border text-sm"
              style={{
                background: theme.colors.background,
                borderColor: `${theme.colors.textMuted}33`,
                color: theme.colors.text,
              }}
            />
          </div>
        ))}

        <button
          onClick={addSection}
          className="px-4 py-2 rounded-lg border-2 border-dashed text-sm font-medium w-full"
          style={{ borderColor: `${accent}66`, color: accent }}
        >
          + Add section
        </button>
      </div>
    </div>
  );
}
